/* Les Gastronomes — keep cart delivery promises current before checkout.
   - Recalculates ETA from the same product metafields used on the PDP.
   - Normalises legacy technical properties into hidden underscore properties.
   - Adds one customer-friendly variable-weight message.
   - Does not alter quantities, prices, variants or Avenue cart behaviour. */
(function () {
  'use strict';

  var WEIGHT_MESSAGE = 'Final price is adjusted to the actual prepared weight. Any overpayment is returned as store credit; if higher, we’ll contact you for the difference.';

  function cartUrl(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
  }

  function dubaiNowParts() {
    var list = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Dubai',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
    }).formatToParts(new Date());
    var out = {};
    list.forEach(function (part) {
      if (part.type !== 'literal') out[part.type] = parseInt(part.value, 10);
    });
    return out;
  }

  function parseTime(value) {
    var m = (value || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return { hour: 23, minute: 59 };
    var hour = parseInt(m[1], 10);
    var minute = parseInt(m[2], 10);
    var period = m[3].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return { hour: hour, minute: minute };
  }

  var dayMap = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6
  };

  var fullFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  var shortFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short'
  });

  function buildCycle(today, parts, cutoffDayName, cutoffTimeText, deliveryStartName, deliveryEndName) {
    var cutoffDay = dayMap[cutoffDayName];
    var deliveryStart = dayMap[deliveryStartName];
    if (typeof cutoffDay === 'undefined' || typeof deliveryStart === 'undefined') return null;

    var cutoffTime = parseTime(cutoffTimeText);
    var daysUntilCutoff = (cutoffDay - today.getUTCDay() + 7) % 7;
    if (daysUntilCutoff === 0) {
      var cutoffPassed = parts.hour > cutoffTime.hour ||
        (parts.hour === cutoffTime.hour && parts.minute >= cutoffTime.minute);
      if (cutoffPassed) daysUntilCutoff = 7;
    }

    var cutoffDate = new Date(today.getTime());
    cutoffDate.setUTCDate(cutoffDate.getUTCDate() + daysUntilCutoff);

    var daysToStart = (deliveryStart - cutoffDay + 7) % 7;
    if (daysToStart === 0) daysToStart = 7;

    var startDate = new Date(cutoffDate.getTime());
    startDate.setUTCDate(startDate.getUTCDate() + daysToStart);

    var endDate = null;
    var deliveryEnd = dayMap[deliveryEndName];
    if (typeof deliveryEnd !== 'undefined') {
      var daysStartToEnd = (deliveryEnd - deliveryStart + 7) % 7;
      endDate = new Date(startDate.getTime());
      endDate.setUTCDate(endDate.getUTCDate() + daysStartToEnd);
    }

    return { cutoffDate: cutoffDate, startDate: startDate, endDate: endDate };
  }

  function nextOperatingDay(date) {
    var result = new Date(date.getTime());
    do { result.setUTCDate(result.getUTCDate() + 1); }
    while (result.getUTCDay() === 0);
    return result;
  }

  function addOperatingDays(date, count) {
    var result = new Date(date.getTime());
    for (var i = 0; i < count; i++) result = nextOperatingDay(result);
    return result;
  }

  function calculateDelivery(config, compact) {
    if (!config) return '';
    var parts = dubaiNowParts();
    var today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    var isPreOrder = config.dataset.preorder === 'true';
    var formatter = compact ? shortFormatter : fullFormatter;

    if (isPreOrder) {
      var cycle1 = buildCycle(today, parts,
        config.dataset.cutoffDay,
        config.dataset.cutoffTime,
        config.dataset.deliveryDay,
        config.dataset.deliveryEndDay
      );
      var cycle2 = buildCycle(today, parts,
        config.dataset.cutoffDay2,
        config.dataset.cutoffTime2,
        config.dataset.deliveryDay2,
        config.dataset.deliveryEndDay2
      );
      var selected = cycle1 && cycle2 ?
        (cycle1.cutoffDate <= cycle2.cutoffDate ? cycle1 : cycle2) :
        (cycle1 || cycle2);
      if (!selected) return '';
      return selected.endDate && selected.endDate.getTime() !== selected.startDate.getTime()
        ? formatter.format(selected.startDate) + ' – ' + formatter.format(selected.endDate)
        : formatter.format(selected.startDate);
    }

    var minDays = parseInt(config.dataset.minDays, 10);
    var maxDays = parseInt(config.dataset.maxDays, 10);
    if (isNaN(minDays)) minDays = 0;
    if (isNaN(maxDays)) maxDays = 1;
    if (maxDays < minDays) maxDays = minDays;

    var base = new Date(today.getTime());
    var isSunday = base.getUTCDay() === 0;
    var afterCutoff = parts.hour >= 15;
    var deferredProcessing = isSunday || afterCutoff;
    if (deferredProcessing) base = nextOperatingDay(base);

    /* The current/next operating day is the processing day. Delivery always
       starts from the following operating day; Sunday is never a delivery day. */
    var deliveryOffset = 1;
    var earliest = addOperatingDays(base, minDays + deliveryOffset);
    var latest = addOperatingDays(base, maxDays + deliveryOffset);
    return earliest.getTime() === latest.getTime()
      ? formatter.format(earliest)
      : formatter.format(earliest) + ' – ' + formatter.format(latest);
  }

  function configMap() {
    var map = {};
    document.querySelectorAll('[data-lg-cart-delivery][data-line-key]').forEach(function (el) {
      if (!map[el.dataset.lineKey]) map[el.dataset.lineKey] = el;
    });
    return map;
  }

  function refreshVisibleDelivery() {
    document.querySelectorAll('[data-lg-cart-delivery]:not([hidden])').forEach(function (el) {
      var output = el.querySelector('[data-lg-cart-delivery-date]');
      if (!output) return;
      var current = calculateDelivery(el, true);
      if (current) output.textContent = current;
    });
  }

  function propertiesEqual(a, b) {
    var ka = Object.keys(a || {}).sort();
    var kb = Object.keys(b || {}).sort();
    if (ka.length !== kb.length) return false;
    for (var i = 0; i < ka.length; i++) {
      if (ka[i] !== kb[i] || String(a[ka[i]]) !== String(b[kb[i]])) return false;
    }
    return true;
  }

  function normalisedProperties(item, config) {
    var props = Object.assign({}, item.properties || {});
    var delivery = calculateDelivery(config, false);
    var isPreOrder = config && config.dataset.preorder === 'true';
    var isVariable = config && config.dataset.variableWeight === 'true';

    if (delivery) props['Estimated delivery'] = delivery;
    if (isPreOrder) {
      props['Delivery type'] = 'Pre-order';
      props['_preorder'] = 'Yes';
    }

    /* Migrate legacy technical properties away from customer-facing checkout. */
    if (props['Price per kg'] && !props['_price_per_kg']) props['_price_per_kg'] = props['Price per kg'];
    if (props['Weight range'] && !props['_weight_range']) props['_weight_range'] = props['Weight range'];
    delete props['Pre-order'];
    delete props['Variable weight'];
    delete props['Price per kg'];
    delete props['Weight range'];

    if (isVariable) {
      props['_variable_weight'] = 'Yes';
      if (props['Estimated weight']) props['_estimated_weight'] = props['Estimated weight'];
      delete props['Estimated weight'];
      props['Weight adjustment'] = WEIGHT_MESSAGE;
    } else {
      delete props['Weight adjustment'];
      delete props['_variable_weight'];
      delete props['_estimated_weight'];
      delete props['_price_per_kg'];
      delete props['_weight_range'];
    }

    return props;
  }

  async function fetchCart() {
    var response = await fetch(cartUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  async function updateLine(item, properties) {
    var response = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: properties
      })
    });
    if (!response.ok) throw new Error('Unable to refresh delivery details');
    return response.json();
  }

  async function syncBeforeCheckout() {
    var map = configMap();
    if (!Object.keys(map).length) return false;

    var cart = await fetchCart();
    var changed = false;

    /* Process one item at a time and re-fetch after each mutation because
       Shopify line keys can change when properties change. */
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var config = map[item.key];
      if (!config) continue;
      var next = normalisedProperties(item, config);
      if (!propertiesEqual(item.properties || {}, next)) {
        await updateLine(item, next);
        changed = true;
        cart = await fetchCart();
        /* The updated line key may have changed; remaining original DOM keys
           still map to their untouched cart lines. */
      }
    }

    return changed;
  }

  var autoSyncBusy = false;
  async function autoSyncCart() {
    if (autoSyncBusy || !Object.keys(configMap()).length) return;
    autoSyncBusy = true;
    try { await syncBeforeCheckout(); }
    catch (error) { console.error('Les Gastronomes cart property refresh:', error); }
    finally { autoSyncBusy = false; refreshVisibleDelivery(); }
  }

  document.addEventListener('DOMContentLoaded', function () {
    refreshVisibleDelivery();
    setTimeout(autoSyncCart, 120);
  });
  window.addEventListener('pageshow', function () {
    refreshVisibleDelivery();
    setTimeout(autoSyncCart, 120);
  });
  document.addEventListener('cartItems', function () {
    setTimeout(function(){ refreshVisibleDelivery(); autoSyncCart(); }, 120);
  });

  /* Final guard before standard Avenue checkout forms. */
  document.addEventListener('submit', async function (event) {
    var form = event.target;
    var checkout = form && form.querySelector('[name="checkout"]');
    if (!checkout || form.dataset.lgDeliveryVerified === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkout.disabled = true;
    checkout.setAttribute('aria-busy', 'true');

    try {
      await syncBeforeCheckout();
    } catch (error) {
      console.error('Les Gastronomes delivery refresh:', error);
      /* Do not trap the customer if the refresh request fails. */
    }

    form.dataset.lgDeliveryVerified = 'true';
    checkout.disabled = false;
    checkout.removeAttribute('aria-busy');
    if (checkout.click) checkout.click();
    else form.submit();
  }, true);

  window.LGRefreshCartDelivery = syncBeforeCheckout;
  window.LGCalculateCartDelivery = calculateDelivery;
})();
